import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Form, FormField, FormTextArea, FormCheckbox } from 'components/form';
import { GlobalScrollLock } from 'components/GlobalScrollLock';
import {
  emailSchema,
  messageSchema,
  nameSchema,
  phoneSchema,
} from 'utils/validators';

import { sendMessage } from '../../api';

import { formatResponseTime } from '../helpers';
import * as styled from '../styles/message-modal';

type Props = {
  token: string;
  services: Kudable.Service[];
  providerName: string;
  acceptsBookings: boolean;
  responseTimeHours: number | null;
  accentColor: string;
  initialMessage?: string;
  onClose: () => void;
};

const getDefaultMessage = (
  services: Kudable.Service[],
  acceptsBookings: boolean,
): string => {
  if (!acceptsBookings)
    return "Hi, I'd like to connect with you. Are you available for a conversation?";
  if (services.length === 1)
    return `Hi, I'm interested in ${services[0].name}. Are you available?`;
  if (services.length > 1)
    return "Hi, I'm interested in your services. Are you available this week?";
  return "Hi, I saw your profile on Kudable. I'd like to learn more about your services.";
};

const messageSchemaRoot = z.object({
  client_name: nameSchema,
  client_phone: phoneSchema,
  client_email: emailSchema,
  phone_is_whatsapp: z.boolean(),
  initial_message: messageSchema,
});
type MessageFormValues = z.infer<typeof messageSchemaRoot>;

export const MessageModal = ({
  token,
  services,
  providerName,
  acceptsBookings,
  responseTimeHours,
  accentColor,
  initialMessage,
  onClose,
}: Props) => {
  const [step, setStep] = useState<Profile.MessageStep>('form');
  const [serviceId, setServiceId] = useState<string | null>(null);
  const [viewToken, setViewToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<MessageFormValues>({
    resolver: zodResolver(messageSchemaRoot),
    mode: 'onBlur',
    defaultValues: {
      client_name: '',
      client_phone: '',
      client_email: '',
      phone_is_whatsapp: false,
      initial_message:
        initialMessage ?? getDefaultMessage(services, acceptsBookings),
    },
  });

  const handleSubmit = (values: MessageFormValues) => {
    setError(null);
    return sendMessage(token, {
      client_name: values.client_name.trim(),
      client_phone: values.client_phone.trim(),
      client_email: values.client_email.trim(),
      phone_is_whatsapp: values.phone_is_whatsapp,
      initial_message: values.initial_message.trim(),
      service_id: serviceId || undefined,
    })
      .then((response) => {
        setViewToken(response.data.thread.view_token);
        setStep('done');
      })
      .catch(() => setError('Could not send message. Please try again.'));
  };

  const responseLabel = formatResponseTime(responseTimeHours);
  const threadUrl = viewToken
    ? `${window.location.origin}/messages/thread/${viewToken}`
    : null;
  const email = form.watch('client_email');

  return (
    <styled.Overlay onClick={onClose}>
      <GlobalScrollLock />
      <styled.Sheet onClick={(event) => event.stopPropagation()}>
        {step === 'form' && (
          <Form form={form} onSubmit={handleSubmit}>
            <styled.TopRow>
              <styled.Title>Send a message</styled.Title>
              <styled.CloseBtn type="button" onClick={onClose}>
                ✕
              </styled.CloseBtn>
            </styled.TopRow>
            {responseLabel && (
              <styled.ResponseHint $accent={accentColor}>
                ⚡ {responseLabel}
              </styled.ResponseHint>
            )}

            <FormField
              name="client_name"
              label="Your name *"
              placeholder="Your name"
            />
            <FormField
              name="client_phone"
              type="tel"
              label="Phone number *"
              placeholder="+1 555 000 0000"
            />
            <FormCheckbox
              name="phone_is_whatsapp"
              label="This number is on WhatsApp"
            />
            <FormField
              name="client_email"
              type="email"
              label="Email address *"
              placeholder="you@example.com"
            />

            {services.length > 1 && (
              <>
                <styled.Label>I'm interested in</styled.Label>
                <styled.ServiceRow>
                  {services.map((service) => (
                    <styled.ServiceChip
                      key={service.id}
                      type="button"
                      $selected={serviceId === service.id}
                      $accent={accentColor}
                      onClick={() =>
                        setServiceId(
                          serviceId === service.id ? null : service.id,
                        )
                      }
                    >
                      {service.name}
                    </styled.ServiceChip>
                  ))}
                </styled.ServiceRow>
              </>
            )}

            <FormTextArea name="initial_message" label="Message *" rows={4} />

            {error && <styled.ErrorText>{error}</styled.ErrorText>}

            <styled.PrimaryBtn
              type="submit"
              $accent={accentColor}
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? 'Sending…' : 'Send message'}
            </styled.PrimaryBtn>
          </Form>
        )}

        {step === 'done' && (
          <styled.SuccessBox>
            <styled.SuccessIcon $accent={accentColor}>✓</styled.SuccessIcon>
            <styled.SuccessTitle>Message sent!</styled.SuccessTitle>
            <styled.SuccessText>
              {providerName} will receive your message right away.
              {responseLabel && <> They typically respond within that time.</>}
            </styled.SuccessText>
            <styled.SuccessText>
              We've sent a confirmation to <strong>{email}</strong>. You'll get
              an email when they reply — and you can reply directly from your
              email too.
            </styled.SuccessText>
            {threadUrl && (
              <styled.LinkBox>
                <styled.LinkLabel>Or check for replies here:</styled.LinkLabel>
                <styled.LinkUrl href={threadUrl} $accent={accentColor}>
                  {threadUrl}
                </styled.LinkUrl>
              </styled.LinkBox>
            )}
            <styled.PrimaryBtn $accent={accentColor} onClick={onClose}>
              Done
            </styled.PrimaryBtn>
          </styled.SuccessBox>
        )}
      </styled.Sheet>
    </styled.Overlay>
  );
};
